import axiosInstance from '../axios/axiosInstance';
import type { PaginatedResponse } from './types';

// Billing Status Enum
export enum BillingStatus {
  QUOTED = 'QUOTED',
  INVOICED = 'INVOICED',
  PAID = 'PAID',
  FREE = 'FREE',
}

export interface Project {
  _id: string;
  title: string;
  status: string;
  workspaceId: string;
  workspaceName: string;
  organizationId: string;
  organizationName: string;
  respondentsMax: number;
  respondentsCount: number;
  conversationsTotal: number;
  conversationsCompleted: number;
  createdAt: string;
  deletedAt?: string;
  isDeleted?: boolean;
  // Billing Status (Super Admin Only)
  billingStatus?: BillingStatus;
  billingStatusUpdatedAt?: string;
  billingStatusUpdatedBy?: {
    _id: string;
    email: string;
    name?: string;
  };
  billingNotes?: string;
  billingAmount?: number;
  billingCurrency?: string;
  billingReference?: string;
  hubspotDealId?: string | null;
  billingInvoicedSnapshot?: { completedCount: number; at: string } | null;
}

/**
 * Partial-update payload for `PATCH /projects/:id/billing` (TELV1-524): any
 * subset of fields may be sent — an absent field is left untouched, an
 * explicit `null` clears it. `billingStatus: null` maps to the "New" status.
 */
export interface UpdateBillingStatusPayload {
  billingStatus?: BillingStatus | null;
  notes?: string | null;
  billingAmount?: number | null;
  billingCurrency?: string | null;
  billingReference?: string | null;
  hubspotDealId?: string | null;
}

export interface BillingHistoryEntry {
  _id: string;
  billingStatus: BillingStatus;
  notes?: string;
  billingAmount?: number;
  billingCurrency?: string;
  billingReference?: string;
  updatedAt: string;
  updatedBy?: {
    _id: string;
    email: string;
    name?: string;
  };
}

export interface BillingHistoryFilters {
  page?: number;
  limit?: number;
}

export type AnalysisRunState = 'loading' | 'done' | 'idle' | 'error';

export interface AnalysisRunResponse {
  state: AnalysisRunState;
  state_id?: string;
}

export type AnalysisOutcome = 'success' | 'partial_success' | 'error';

export interface AnalysisRunStateResponse {
  state: AnalysisRunState;
  numberOfUnRunConversations: number;
  hasDeletionsSinceLastRun?: boolean;
  /** Derived outcome for terminal states; undefined while loading/idle */
  outcome?: AnalysisOutcome;
}

export type AnalysisRunPhase =
  | 'idle'
  | 'per_question_pages'
  | 'meta_synthesis'
  | 'overall'
  | 'complete';

export interface AnalysisRunProgress {
  phase: AnalysisRunPhase;
  current?: number;
  total?: number;
  started_at?: string;
  elapsed_ms?: number;
  /** Set for 5 minutes after a run completes */
  completed_at?: string;
}

/**
 * A project's report filter, as exposed to the super-admin portal's
 * single-filter re-run scope picker (TELV1-500) — `{_id, name}` only, no
 * `customFields`/`created_by`. Not to be confused with `ProjectFilters`
 * below (the project *list* query params).
 */
export interface ProjectFilterOption {
  _id: string;
  name: string;
}

export interface ProjectFilters {
  page?: number;
  limit?: number;
  search?: string;
  includeDeleted?: boolean;
  sortBy?:
    | 'title'
    | 'status'
    | 'billingStatus'
    | 'respondentsCount'
    | 'conversationsTotal'
    | 'createdAt'
    | 'organizationName'
    | 'workspaceName';
  sortOrder?: 'asc' | 'desc';
}

export const projectsApi = {
  /**
   * Get all projects with filters and pagination
   */
  getAll: async (params: ProjectFilters): Promise<PaginatedResponse<Project>> => {
    const { data } = await axiosInstance.get<PaginatedResponse<Project>>('/projects', { params });
    return data;
  },

  // ==================== Billing Status ====================

  /**
   * Update project billing status
   */
  updateBillingStatus: async (
    id: string,
    payload: UpdateBillingStatusPayload,
  ): Promise<Project> => {
    const { data } = await axiosInstance.patch<Project>(`/projects/${id}/billing`, payload);
    return data;
  },

  /**
   * Get project with billing details
   */
  getWithBilling: async (id: string): Promise<Project> => {
    const { data } = await axiosInstance.get<Project>(`/projects/${id}/billing`);
    return data;
  },

  /**
   * Get project billing history
   */
  getBillingHistory: async (
    id: string,
    params?: BillingHistoryFilters,
  ): Promise<{
    data: BillingHistoryEntry[];
    total: number;
    page: number;
    totalPages: number;
    limit: number;
  }> => {
    const { data } = await axiosInstance.get(`/projects/${id}/billing/history`, { params });
    return data;
  },

  /**
   * Restore a soft-deleted project
   */
  restore: async (id: string, organizationId: string, workspaceId: string): Promise<Project> => {
    const { data } = await axiosInstance.patch<Project>(
      `/organizations/${organizationId}/workspaces/${workspaceId}/projects/${id}/restore`,
    );
    return data;
  },

  // ==================== Analysis Re-run ====================

  /**
   * Force a full re-run of the project's analysis pipeline, even when
   * there are no new conversations (which is when the dashboard UI
   * refuses to offer one). The analysis is regenerated in the project's
   * CURRENT analysis_language.
   *
   * `filterId` opts into a single-filter re-run (TELV1-500) instead of the
   * default main/unfiltered analysis. Omitting it is byte-for-byte
   * identical to the pre-TELV1-500 request.
   */
  forceRerunAnalysis: async (id: string, filterId?: string): Promise<AnalysisRunResponse> => {
    const { data } = await axiosInstance.post<AnalysisRunResponse>(
      `/analyzer/run/${id}/force`,
      undefined,
      filterId ? { params: { filter_id: filterId } } : undefined,
    );
    return data;
  },

  /**
   * List the project's report filters, for the single-filter re-run scope
   * picker (TELV1-500). Empty array when the project has no filters — the
   * picker degrades to "Main analysis" only.
   */
  listFilters: async (id: string): Promise<ProjectFilterOption[]> => {
    const { data } = await axiosInstance.get<ProjectFilterOption[]>(`/analyzer/run/${id}/filters`);
    return data;
  },

  /**
   * Fine-grained pipeline progress for a running (or recently completed)
   * analysis. `phase: 'idle'` when nothing is running.
   */
  getAnalysisRunProgress: async (id: string): Promise<AnalysisRunProgress> => {
    const { data } = await axiosInstance.get<AnalysisRunProgress>(`/analyzer/run/${id}/progress`);
    return data;
  },

  /**
   * Run state with the authoritative `outcome` (success / partial_success /
   * error) for terminal states — used to report how a watched run ended,
   * since the progress endpoint can't distinguish error from completion.
   */
  getAnalysisRunState: async (id: string): Promise<AnalysisRunStateResponse> => {
    const { data } = await axiosInstance.get<AnalysisRunStateResponse>(`/analyzer/run/${id}/state`);
    return data;
  },
};
